/**
 * DTemplate Example Slide
 * One of the homepage's demos as one of the template's worked examples:
 * the wish, what the mom gets, and what each kind added to it
 */

import React from 'react';
import styled from 'styled-components';
import { useReducedMotion } from 'framer-motion';
import { ENGINE_ORDER } from '@components/wish-story/engines';
import { FREEZE_FOR_CAPTURE } from '@components/motion/scroll-motion';
import { KIND_NAMES } from '@components/press/pitch-deck/slides/deck-kinds';
import { DemoPhone } from '@components/press/pitch-deck/slides/demo-phone';
import { DECK_DEMOS, type DeckDemo } from '@components/press/pitch-deck/slides/dtemplate/demos';
import { SplitSlide } from '@components/press/pitch-deck/slides/dtemplate/frame';
import { typography } from '@config/typography';
import { colors } from '@config/colors';

/*
 * The template's examples (its slides 14–16) each show the product
 * catching one concrete problem. Ours are the homepage's demos — the
 * same wishes, recordings and "what went in" — each answering a real
 * mom's wish, the recording playing beside it.
 */

export const ExampleSlide: React.FC<{ demo: DeckDemo }> = ({ demo }) => {
  const still = (useReducedMotion() ?? false) || FREEZE_FOR_CAPTURE;
  return (
    <SplitSlide
      section={`Example · ${demo.title}`}
      question="What does it look like for a real customer?"
      headline={<>“{demo.wish}”</>}
      points={[
        <>
          <strong>What she gets:</strong> {demo.screen.label}
        </>,
        ...ENGINE_ORDER.map((kind) => (
          <>
            <strong>+ {KIND_NAMES[kind]}:</strong> {demo.built[kind]}
          </>
        )),
      ]}
      picture={
        <Stage>
          <DemoPhone screen={demo.screen} startAfter={still ? null : 1.1} width={380} still={demo.still} />
          <Caption>{demo.title}, in the app</Caption>
        </Stage>
      }
    />
  );
};

/** One example slide per live demo, in the homepage's order. */
export const EXAMPLE_SLIDES: ReadonlyArray<{ demo: DeckDemo; component: React.FC }> = DECK_DEMOS.map((demo) => {
  const Example: React.FC = () => <ExampleSlide demo={demo} />;
  Example.displayName = `Example(${demo.key})`;
  return { demo, component: Example };
});

const Stage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Caption = styled.div`
  margin-top: -40px;
  font-family: ${typography.fontFamily.small};
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.brand.exxxtraDark};
`;
